import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card, Row, Col, Statistic, Table, Tag, Progress, Button, Select, Spin, message,
  Segmented, Alert, Divider, Badge, Empty,
} from 'antd';
import {
  SafetyCertificateOutlined, DollarOutlined, ThunderboltOutlined, SyncOutlined,
} from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { storage, monitoring, meters } from '../../../services/api';
import { isViewer } from '../../../utils/auth';

const MODE_LABELS = { safety: '安全模式', economic: '经济模式', auto: '智能协同' };
const STRATEGY_MAP = { peak_valley: '削峰填谷', demand: '需量管理', dispatch: '响应调度' };
const STRATEGY_REVERSE = Object.fromEntries(Object.entries(STRATEGY_MAP).map(([k, v]) => [v, k]));

export default function StoragePage() {
  // viewer 只读：策略切换与执行控件禁用（后端 403 兜底）
  const readOnly = isViewer();
  const [loading, setLoading] = useState(true);
  const [systems, setSystems] = useState([]);
  const [strategy, setStrategy] = useState(null);
  const [dualMode, setDualMode] = useState(null);
  const [currentStrategy, setCurrentStrategy] = useState('peak_valley');
  const [operationMode, setOperationMode] = useState('auto');
  const [socCurves, setSocCurves] = useState([]);
  // 经济调度（日前优化建议，不轮询：生成/进入页面时加载一次）
  const [econPlan, setEconPlan] = useState(null);
  const [econLoading, setEconLoading] = useState(false);

  const inFlightRef = useRef(false);

  // 次日日期 YYYY-MM-DD（本地时区）
  const tomorrowStr = () => {
    const d = new Date(Date.now() + 24 * 3600 * 1000);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const loadEconPlan = async () => {
    try {
      const res = await storage.econLatest();
      setEconPlan(res.data || null);
    } catch {
      // 计划加载失败不阻塞页面其余部分
    }
  };

  const handleGenerateEconPlan = async () => {
    setEconLoading(true);
    try {
      const res = await storage.econPlanGenerate({ date: tomorrowStr() });
      setEconPlan(res.data);
      message.success(`次日（${res.data?.target_date}）优化计划已生成（仅建议，不下发执行）`);
    } catch (err) {
      message.error(err.message || '生成优化计划失败');
    } finally {
      setEconLoading(false);
    }
  };

  const loadData = async () => {
    // 轮询竞态守卫：上一轮未返回时跳过本轮，避免慢请求重叠乱序
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const [listRes, strategyRes, dualRes, metersRes] = await Promise.all([
        storage.list(),
        storage.strategy(),
        storage.dualMode(),
        meters.list({ type: '储能变流器' }),
      ]);

      const pcsIds = (metersRes.data || []).map((m) => m.id);
      let realtimeMap = {};
      if (pcsIds.length > 0) {
        const rtRes = await monitoring.batchRealtime({ meter_ids: pcsIds });
        (rtRes.data || []).forEach((item) => {
          realtimeMap[item.meter_id] = item;
        });
      }

      // SOC 历史曲线（真实遥测）
      if (pcsIds.length > 0) {
        const curveResList = await Promise.all(
          pcsIds.map((id) => monitoring.curve({ meter_id: id, hours: 24, fields: 'soc' }).catch(() => null))
        );
        setSocCurves(curveResList.map((r) => r?.data).filter(Boolean));
      } else {
        setSocCurves([]);
      }

      const rows = (listRes.data || []).map((sys, idx) => {
        const pcsMeter = metersRes.data?.[idx];
        const rt = pcsMeter ? realtimeMap[pcsMeter.id] : null;
        const power = rt ? (rt.discharge_power > 0 ? rt.discharge_power : -(rt.charge_power || 0)) : 0;
        return {
          key: sys.id,
          name: sys.name,
          capacity: parseFloat(sys.rated_capacity),
          power: parseFloat(sys.rated_power),
          // 无遥测数据时显示 "—"，不用兜底常数伪装
          soc: rt?.soc ?? null,
          soh: sys.soh != null ? parseFloat(sys.soh) : null,
          current_power: power,
          status: rt?.status || '待机',
        };
      });

      setSystems(rows);
      setStrategy(strategyRes.data);
      setDualMode(dualRes.data);

      if (dualRes.data?.operation_mode) setOperationMode(dualRes.data.operation_mode);
      const st = dualRes.data?.strategy_type || strategyRes.data?.strategy;
      if (st && STRATEGY_REVERSE[st]) setCurrentStrategy(STRATEGY_REVERSE[st]);
    } catch (err) {
      message.error(err.message || '加载储能数据失败');
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadEconPlan();
    const timer = setInterval(loadData, 10000);
    return () => clearInterval(timer);
  }, []);

  const handleStrategyChange = async (value) => {
    try {
      await storage.setStrategy({ strategy_type: STRATEGY_MAP[value] });
      setCurrentStrategy(value);
      message.success(`子策略已切换: ${STRATEGY_MAP[value]}`);
      loadData();
    } catch (err) {
      // 501（控制通道未接入）拦截器已统一提示，不再重复报错
      if (err.response?.status !== 501) message.error(err.message || '策略切换失败');
    }
  };

  const handleModeChange = async (mode) => {
    try {
      await storage.setDualMode({ operation_mode: mode });
      setOperationMode(mode);
      message.success(`运行模式已切换: ${MODE_LABELS[mode]}`);
      loadData();
    } catch (err) {
      // 501（控制通道未接入）拦截器已统一提示，不再重复报错
      if (err.response?.status !== 501) message.error(err.message || '模式切换失败');
    }
  };

  const socValues = systems.filter((x) => x.soc != null).map((x) => x.soc);
  const avgSoc = socValues.length ? socValues.reduce((s, v) => s + v, 0) / socValues.length : null;
  const totalCapacity = systems.reduce((s, x) => s + x.capacity, 0);
  const totalPower = systems.reduce((s, x) => s + x.current_power, 0);
  const sohValues = systems.filter((x) => x.soh != null).map((x) => x.soh);
  const avgSoh = sohValues.length ? sohValues.reduce((s, v) => s + v, 0) / sohValues.length : null;

  const actionColor = (action) => {
    if (action === '充电') return 'blue';
    if (action === '放电') return 'volcano';
    return 'default';
  };

  const socBandOption = useMemo(() => {
    if (!dualMode) return {};
    const safety = dualMode.safety_mode?.soc_band || {};
    const economic = dualMode.economic_mode?.soc_band || {};
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['安全区间', '经济区间', '当前SOC'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'value', min: 0, max: 100, name: 'SOC %' },
      yAxis: { type: 'category', data: ['运行边界'] },
      series: [
        {
          name: '安全区间',
          type: 'bar',
          stack: 'band',
          data: [safety.max - safety.min],
          itemStyle: { color: 'rgba(82,196,26,0.35)' },
          barWidth: 24,
        },
        {
          name: '经济区间',
          type: 'bar',
          data: [economic.max - economic.min],
          itemStyle: { color: 'rgba(24,144,255,0.35)' },
          barWidth: 16,
        },
        {
          name: '当前SOC',
          type: 'scatter',
          data: avgSoc != null ? [[avgSoc, 0]] : [],
          symbolSize: 18,
          itemStyle: { color: '#fa8c16' },
        },
      ],
    };
  }, [dualMode, avgSoc]);

  const hasSocCurve = socCurves.some((c) => (c.points || []).some((p) => p.soc != null));

  const chartOption = {
    tooltip: { trigger: 'axis' },
    legend: { data: socCurves.map((c) => c.meter_name || `表计#${c.meter_id}`) },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: (socCurves[0]?.points || []).map((p) => p.time) },
    yAxis: { type: 'value', name: '%', max: 100 },
    series: socCurves.map((c) => ({
      name: c.meter_name || `表计#${c.meter_id}`,
      type: 'line',
      smooth: true,
      connectNulls: true,
      data: (c.points || []).map((p) => p.soc),
    })),
  };

  // 经济调度计划图：充放电功率柱状（正放负充）+ 电价曲线叠加 + SOC 轨迹
  const econChartOption = useMemo(() => {
    const schedule = econPlan?.schedule || [];
    return {
      tooltip: { trigger: 'axis' },
      legend: { data: ['充放电功率', '电价', '计划SOC'] },
      grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
      xAxis: { type: 'category', data: schedule.map((s) => `${String(s.hour).padStart(2, '0')}:00`) },
      yAxis: [
        { type: 'value', name: 'kW' },
        { type: 'value', name: '元/kWh / SOC%', min: 0, max: 100 },
      ],
      series: [
        {
          name: '充放电功率',
          type: 'bar',
          data: schedule.map((s) => s.power_kw),
          itemStyle: { color: (p) => (p.value >= 0 ? '#fa541c' : '#1890ff') },
        },
        {
          name: '电价',
          type: 'line',
          yAxisIndex: 1,
          step: 'end',
          data: schedule.map((s) => s.price),
          itemStyle: { color: '#faad14' },
        },
        {
          name: '计划SOC',
          type: 'line',
          yAxisIndex: 1,
          smooth: true,
          data: schedule.map((s) => s.soc_pct),
          itemStyle: { color: '#52c41a' },
          lineStyle: { type: 'dashed' },
        },
      ],
    };
  }, [econPlan]);

  const columns = [
    { title: '储能系统', dataIndex: 'name' },
    { title: '额定容量(kWh)', dataIndex: 'capacity' },
    { title: '额定功率(kW)', dataIndex: 'power' },
    {
      title: 'SOC(%)',
      dataIndex: 'soc',
      render: (v) => (v == null ? '—' : <Progress percent={v} size="small" status={v > 80 ? 'exception' : 'active'} />),
    },
    { title: 'SOH(%)', dataIndex: 'soh', render: (v) => (v == null ? '—' : `${v.toFixed(1)}%`) },
    {
      title: '当前功率(kW)',
      dataIndex: 'current_power',
      render: (v) => (
        <Tag color={v > 0 ? 'green' : v < 0 ? 'blue' : 'default'}>
          {v > 0 ? `放电 ${v.toFixed(1)}` : v < 0 ? `充电 ${Math.abs(v).toFixed(1)}` : '待机'}
        </Tag>
      ),
    },
    { title: '状态', dataIndex: 'status', render: (v) => <Tag color={v === '放电' ? 'volcano' : 'blue'}>{v}</Tag> },
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const active = dualMode?.active_strategy;
  const safety = dualMode?.safety_mode;
  const economic = dualMode?.economic_mode;

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={6}><Card size="small"><Statistic title="总储能容量" value={totalCapacity} suffix="kWh" /></Card></Col>
        <Col span={6}>
          <Card size="small">
            <Statistic
              title="综合SOC"
              value={avgSoc ?? '—'}
              suffix={avgSoc != null ? '%' : ''}
              precision={1}
              valueStyle={{ color: avgSoc > 80 ? '#faad14' : '#52c41a' }}
            />
            {avgSoc != null && <Progress percent={avgSoc} size="small" status={avgSoc > 80 ? 'exception' : 'active'} />}
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="充放电功率" value={totalPower} suffix="kW" precision={1} valueStyle={{ color: '#1890ff' }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card size="small">
            <Statistic title="平均SOH" value={avgSoh ?? '—'} suffix={avgSoh != null ? '%' : ''} precision={1} valueStyle={{ color: '#52c41a' }} />
          </Card>
        </Col>
      </Row>

      <Card
        title="安全 / 经济双模式策略"
        style={{ marginTop: 16 }}
        size="small"
        extra={(
          <Segmented
            value={operationMode}
            onChange={handleModeChange}
            disabled={readOnly}
            options={[
              { label: '安全模式', value: 'safety', icon: <SafetyCertificateOutlined /> },
              { label: '经济模式', value: 'economic', icon: <DollarOutlined /> },
              { label: '智能协同', value: 'auto', icon: <SyncOutlined /> },
            ]}
          />
        )}
      >
        {active && (
          <Alert
            type={active.mode === 'safety' ? 'warning' : 'info'}
            showIcon
            message={`当前生效：${MODE_LABELS[active.mode] || active.mode} → ${active.recommended_action}`}
            description={active.resolution}
            style={{ marginBottom: 16 }}
          />
        )}

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card
              size="small"
              type="inner"
              title={<><SafetyCertificateOutlined style={{ color: '#52c41a' }} /> 电能力体系 · 安全模式</>}
            >
              <div style={{ marginBottom: 8 }}>
                建议动作：<Tag color={actionColor(safety?.recommended_action)}>{safety?.recommended_action}</Tag>
                <Badge count={`风险${safety?.risk_level || '低'}`} style={{ marginLeft: 8 }} />
              </div>
              <div style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>{safety?.reason}</div>
              <div style={{ fontSize: 12, color: '#999' }}>
                SOC 区间 {safety?.soc_band?.min}% ~ {safety?.soc_band?.max}% · 备用 ≥ {safety?.soc_band?.reserve}%
              </div>
              <Divider style={{ margin: '12px 0' }} />
              {(safety?.constraints || []).map((c) => (
                <Tag key={c} style={{ marginBottom: 4 }}>{c}</Tag>
              ))}
            </Card>
          </Col>
          <Col span={12}>
            <Card
              size="small"
              type="inner"
              title={<><DollarOutlined style={{ color: '#1890ff' }} /> 电能量体系 · 经济模式</>}
            >
              <div style={{ marginBottom: 8 }}>
                建议动作：<Tag color={actionColor(economic?.recommended_action)}>{economic?.recommended_action}</Tag>
                <Tag color="gold" style={{ marginLeft: 8 }}>
                  预估收益 ¥{economic?.expected_revenue_yuan ?? 0}
                </Tag>
              </div>
              <div style={{ color: '#666', fontSize: 13, marginBottom: 12 }}>{economic?.reason}</div>
              <div style={{ fontSize: 12, color: '#999' }}>
                峰谷价差 ¥{economic?.arbitrage_spread}/kWh · 当前电价 ¥{economic?.current_price}/kWh
              </div>
              <Divider style={{ margin: '12px 0' }} />
              <Tag color="blue">{economic?.strategy_type || '削峰填谷'}</Tag>
              <Tag>{economic?.time_slot === 'peak' ? '高峰' : economic?.time_slot === 'valley' ? '低谷' : '平段'}</Tag>
            </Card>
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={12}>
            <Card size="small" type="inner" title="SOC 运行边界">
              <ReactEChartsCore option={socBandOption} style={{ height: 120 }} />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small" type="inner" title="策略控制">
              <div style={{ marginBottom: 12 }}>
                <ThunderboltOutlined /> 子策略：
                <Select value={currentStrategy} style={{ width: 180, marginLeft: 8 }} onChange={handleStrategyChange} disabled={readOnly}>
                  <Select.Option value="peak_valley">削峰填谷</Select.Option>
                  <Select.Option value="demand">需量管理</Select.Option>
                  <Select.Option value="dispatch">响应调度</Select.Option>
                </Select>
              </div>
              <div style={{ marginBottom: 12, fontSize: 13, color: '#666' }}>
                {strategy?.reason}
              </div>
              <Button type="primary" onClick={() => handleStrategyChange(currentStrategy)} disabled={readOnly}>
                执行当前策略
              </Button>
              {dualMode?.telemetry && (
                <div style={{ marginTop: 12, fontSize: 12, color: '#999' }}>
                  园区需量 {dualMode.telemetry.grid_demand_kw} kW · 电池温度 {dualMode.telemetry.max_battery_temp_c}°C
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Card>

      <Card
        title="储能经济调度 · 日前优化建议"
        style={{ marginTop: 16 }}
        size="small"
        extra={(
          <Button type="primary" loading={econLoading} onClick={handleGenerateEconPlan} disabled={readOnly}>
            生成次日优化计划
          </Button>
        )}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="本计划为日前优化建议，不会自动下发到储能设备执行"
          description="基于负荷/光伏/分时电价预测计算，预测偏差会影响实际收益；收益口径为峰谷价差套利，未含需量收益与补贴。"
        />
        {econPlan ? (
          <>
            <Row gutter={[16, 16]}>
              <Col span={6}>
                <Card size="small" type="inner">
                  <Statistic
                    title={`预期日套利收益（${econPlan.target_date}）`}
                    value={econPlan.summary?.expected_revenue_yuan ?? 0}
                    suffix="元"
                    valueStyle={{ color: '#cf1322' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" type="inner">
                  <Statistic title="优化前日电费" value={econPlan.summary?.cost_before_yuan ?? 0} suffix="元" />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" type="inner">
                  <Statistic
                    title="优化后日电费"
                    value={econPlan.summary?.cost_after_yuan ?? 0}
                    suffix="元"
                    valueStyle={{ color: '#3f8600' }}
                  />
                </Card>
              </Col>
              <Col span={6}>
                <Card size="small" type="inner">
                  <Statistic
                    title="充电量 / 放电量"
                    value={econPlan.summary?.charge_kwh ?? 0}
                    suffix={`/ ${econPlan.summary?.discharge_kwh ?? 0} kWh`}
                  />
                </Card>
              </Col>
            </Row>
            <div style={{ margin: '12px 0', fontSize: 12, color: '#999' }}>
              储能参数：容量 {econPlan.params?.capacity_kwh} kWh · 功率 {econPlan.params?.power_kw} kW ·
              SOC {econPlan.params?.soc_min_pct}%~{econPlan.params?.soc_max_pct}% ·
              初始 SOC {econPlan.params?.initial_soc_pct}% ·
              充/放效率 {econPlan.params?.eff_charge}/{econPlan.params?.eff_discharge} ·
              生成于 {econPlan.created_at ? new Date(econPlan.created_at).toLocaleString() : '—'}
            </div>
            {(econPlan.warnings || []).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                {econPlan.warnings.map((w) => <Tag key={w} color="orange">{w}</Tag>)}
              </div>
            )}
            <ReactEChartsCore option={econChartOption} style={{ height: 300 }} />
          </>
        ) : (
          <Empty description="暂无优化计划，点击右上角生成次日计划" style={{ padding: '40px 0' }} />
        )}
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card title="SOC变化曲线 (24h)" size="small">
            {hasSocCurve
              ? <ReactEChartsCore option={chartOption} style={{ height: 280 }} />
              : <Empty description="暂无历史遥测数据" style={{ padding: '60px 0' }} />}
          </Card>
        </Col>
      </Row>

      <Card title="储能系统列表" style={{ marginTop: 16 }} size="small">
        <Table columns={columns} dataSource={systems} pagination={false} size="small" />
      </Card>
    </div>
  );
}
